import { getI18n } from '@/plugins/i18n'
import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

import { useValidation } from '@/composables/validation'
import { useSubjectService } from '@/services/subject.service'

export const useColumns = (
  editors,
  currentVehicleFuelType = {},
  currentSupplierBrandId = {},
  currentVehicleTypeId = {},
  currentCountryId = {},
  currentStateId = {},
  currentVatCountryId = {},
  currentVatCountryCode = {},
  currentCustomerTypeId = {},
  currentCustomerSubTypeId = {},
  currentInfoBikeBrandCode = {},
  currentInfoBikeModelCode = {},
  setCellValueZipCode,
  setCellValueCity
) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const portalStore = usePortalStore()

  const { setSubjectType, isVatCountryIta, isNameExtMandatory } = useSubjectService()
  const { validateTaxCode } = useValidation()

  const getSubjectTypes = (options) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', null],
      sort: 'name',
    }
  }

  const getSubjectSubTypes = (option) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', option.data?.customer?.subject_type_ref || 0],
      sort: 'name',
    }
  }

  function validateEnrolmentDate(e) {
    const currentVehicleType = portalStore.vehicleTypes.find(vt => vt.id === currentVehicleTypeId.value)
    if (!currentVehicleType) {
      return true
    }

    const vehicleTypeEnrolmentMandatory = currentVehicleType?.enrolment_date_mandatory || false

    if (!vehicleTypeEnrolmentMandatory) {
      return true
    }

    return e.value
  }

  function validatePlateNumberNoSpaces(e) {
    if (registryStore.companyData.vehicle_plate_allow_blank_spaces) {
      return true
    }

    const value = e.value || ''
    return !/\s/.test(value)
  }

  function validateMaxVinLength(e) {
    const value = e.value || ''
    return value.length <= 17
  }

  function validateMinVinLength(e) {
    const value = e.value || ''

    const currentSupplierBrand = registryStore.supplierBrands.find(sb => sb.id === currentSupplierBrandId.value)
    const currentVehicleType = portalStore.vehicleTypes.find(vt => vt.id === currentVehicleTypeId.value)

    if (!currentSupplierBrand && !currentVehicleType) {
      return true
    }

    const supplierBrandCheck = currentSupplierBrand?.vin_length_check || false
    const vehicleTypeCheck = currentVehicleType?.vin_length_check || false

    if (!supplierBrandCheck && !vehicleTypeCheck) {
      return true
    }
    return value.length === 17
  }
  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '4rem',
      visibleIndex: 0,
      fixed: false
    },
    dataGridColumn.useAttachmentColumn(),
    {
      caption: i18n.t('common.status'),
      dataField: 'status',
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedVehicleStatusTypes() }),
      validationRules: [{type: 'required'}]
    },
    { dataField: 'id', caption: i18n.t('common.protocol'), sortOrder: 'asc', width: '4rem', validationRules: [{ type: 'required' }] },
    {
      dataField: 'is_partial',
      cellTemplate: 'isPartialCellTemplate',
      visible: false,
      caption: i18n.t('common.partial'),
    },
    {
      dataField: 'infoBike.plate_number',
      caption: ' ',
      editCellTemplate: 'infoBikePlateNumberEditor',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useInfoBikeBrandColumn(
      {
        visible: false,
        showInColumnChooser: false
      }
    ),
    dataGridColumn.useInfoBikeModelColumn({
      brand_code: currentInfoBikeBrandCode,
      visible: false,
      showInColumnChooser: false
    }),
    dataGridColumn.useInfoBikeVariantColumn({
      model_code: currentInfoBikeModelCode,
      visible: false,
      showInColumnChooser: false
    }),
    {
      dataField: 'infoBike.infoBike',
      caption: i18n.t('common.infoBike'),
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.infoBike'),
      dataField: 'infoBikeCodeFull',
      visible: false,
      showInColumnChooser: false,
      editorOptions: {
        readOnly: true,
      },
    },
    {
      caption: i18n.t('common.enginePowerKw'),
      dataField: 'engine_power_kw',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.co2emissions'),
      dataField: 'co2_emissions',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'plate_number',
      caption: i18n.t('common.plate'),
      validationRules: [
        { type: 'required' },
        {
          type: 'custom',
          reevaluate: true,
          validationCallback: validatePlateNumberNoSpaces,
          message: i18n.t('error.validation.noBlankSpacesAllowed'),
        }
      ],
    },
    dataGridColumn.useSupplierBrandColumn({validationRules: [{ type: 'required' }]}),
    {
      caption: i18n.t('common.vin'),
      dataField: 'vin',
      cellTemplate: 'serviceHistoryRef',
      width: '12rem',
      validationRules: [
        { type: 'required' },
        {
          type: 'custom',
          reevaluate: true,
          validationCallback: validateMaxVinLength,
          message: i18n.t('error.validation.too_long', { max: 17 }),
        },
        {
          type: 'custom',
          reevaluate: true,
          validationCallback: validateMinVinLength,
          message: i18n.t('error.validation.too_short', { min: 17 }),
        },
      ],
    },
    {
      caption: i18n.t('common.engineCode'),
      dataField: 'engine_code',
    },
    {
      caption: i18n.t('common.description'),
      dataField: 'name',
      validationRules: [{ type: 'required' }],
    },
    {
      caption: i18n.t('common.color'),
      dataField: 'color',
    },
    {
      caption: i18n.t('common.vehicleType'),
      dataField: 'vehicle_type',
      lookup: dataGrid.useLookup({ store: portalStore.getLocalizedVehicleTypes() }),
      setCellValue: (rowData, value) => {
        rowData.vehicle_type = value
        currentVehicleTypeId.value = value
      },
    },
    {
      caption: i18n.t('common.vehicleFuelType'),
      dataField: 'fuel_type',
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedVehicleFuelTypes() }),
      setCellValue: (rowData, value) => {
        rowData.fuel_type = value
        currentVehicleFuelType.value = value
      },
    },
    {
      caption: i18n.t('common.vehicleEmissionStandardType'),
      dataField: 'emission_standard',
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedVehicleEmissionStandardTypes() }),
    },
    {
      caption: i18n.t('common.vehicleTransmissionType'),
      dataField: 'transmission_type',
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedVehicleTransmissionTypes() }),
    },
    dataGridColumn.useEngineSizeColumn({ validationRules: [{ type: 'required' }], visible: true, showInColumnChooser: true }),
    {
      caption: i18n.t('common.note'),
      dataField: 'notes',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerColumn({
      dataField: 'customer_id',
      editCellTemplate: 'customerEditor',
      caption: i18n.t('common.owner'),
      setCellValue: (newData, value) => {
        dataGridColumn.useCustomerColumn({dataField: 'customer_id'}).setCellValue(newData, value)
        newData.customer = value || {}
        currentCountryId.value = newData.customer?.address?.country
        currentStateId.value = newData.customer?.address?.state
      }
    }),
    dataGridColumn.useCustomerColumn({
      dataField: 'previous_customer_id',
      caption: i18n.t('common.previousOwner'),
      editCellTemplate: 'customerEditor',
    }),
    dataGridColumn.useVehicleKmColumn({ dataField: 'km', visible: true, showInColumnChooser: true }),
    {
      caption: i18n.t('common.enrollmentDate'),
      dataField: 'enrollment_date',
      dataType: 'date',
      validationRules: [
        {
          type: 'custom',
          reevaluate: true,
          validationCallback: validateEnrolmentDate,
          message: `${i18n.t('common.enrollmentDate')} ${i18n.t('error.validation.required')}`,
        }
      ]
    },
    {
      caption: i18n.t('common.roadTaxDueDate'),
      dataField: 'road_tax_due_date',
      dataType: 'date',
    },
    {
      caption: i18n.t('common.revisionDueDate'),
      dataField: 'revision_due_date',
      dataType: 'date',
    },
    {
      caption: `${i18n.t('common.customer')} ${i18n.t('common.vatCountry')}`,
      dataField: 'customer.vat_country_id',
      editCellTemplate: 'vatCountryEditor',
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        newData.customer.vat_country_id = value?.id
        editors['customer.vat_number'].option('disabled', !isVatCountryIta(newData.customer?.vat_country_id))
      },
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerVatNumberColumn({ vatCountryCode: currentVatCountryCode.value, vatCountryId: currentVatCountryId.value, subjectSubTypeId: currentCustomerSubTypeId.value }),
    dataGridColumn.useTaxCodeColumn(
      {
        dataField: 'customer.tax_code',
        caption: i18n.t('common.taxCode'),
        validationRules: [{
          type: 'custom',
          reevaluate: true,
          validationCallback: (e) => !registryStore.companyData.subject_validate_vat_and_fiscal_code || validateTaxCode(e.value, currentVatCountryCode.value),
        }, {
          type: 'required'
        }],
      }
    ),
    {
      dataField: 'customer.subject_type_ref',
      caption: i18n.t('common.personType'),
      lookup: dataGrid.useLookup({ dataSource: getSubjectTypes }),
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        currentCustomerTypeId.value = value
        setSubjectType(newData.customer, currentData, value)
        editors['customer.vat_number'].option('disabled', !isVatCountryIta(currentData.customer?.vat_country_id))
      },
      validationRules: [{ type: 'required' }],
      visible: false,
    },
    {
      dataField: 'customer.subject_sub_type_ref',
      caption: `${i18n.t('common.category')} / ${i18n.t('common.gender')}`,
      lookup: dataGrid.useLookup({ dataSource: getSubjectSubTypes }),
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        setSubjectType(newData.customer, currentData, currentData.customer?.subject_type_ref, value)
      },
      validationRules: [{ type: 'required' }],
      visible: false,
    },
    { dataField: 'customer.name', visible: false, validationRules: [{ type: 'required' }], caption: `${i18n.t('common.customer')} ${i18n.t('common.lastName')}`},
    {
      dataField: 'customer.name_ext',
      visible: false,
      caption: `${i18n.t('common.customer')} ${i18n.t('common.name')}`,
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        message: i18n.t('common.required'),
        validationCallback: (e) => !isNameExtMandatory(e.data?.customer?.subject_type_ref) || !!e.value,
      }],
    },
    {
      dataField: 'customer.address.country_name', caption: i18n.t('common.country'),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerAddressCountryColumn({ setCellValue: (newData, value, currentData) => { dataGridColumn.useAddressRef().setCellValueCountry(newData, value, currentData) } }),
    {
      dataField: 'customer.address.state_name', caption: i18n.t('common.state'),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerAddressStateColumn({ setCellValue: (newData, value, currentData) => { dataGridColumn.useAddressRef().setCellValueState(newData, value, currentData) } }),
    {
      dataField: 'customer.address.city_name', caption: i18n.t('common.city'),
      visible: false,
      showInColumnChooser: false
    },
    dataGridColumn.useCustomerAddressCityColumn({ setCellValue: (newData, value, currentData) => { setCellValueCity(newData, value, currentData) } }),
    dataGridColumn.useCustomerAddressZipCodeColumn({setCellValue: (newData, value, currentData) => { setCellValueZipCode(newData, value, currentData, 'customer', false) }}),
    dataGridColumn.useCustomerAddressStreetColumn(),
    { dataField: 'customer.phone', caption: i18n.t('common.phone') },
    { dataField: 'customer.mobile', caption: i18n.t('common.mobile') },
    dataGridColumn.useEmailColumn({ dataField: 'customer.email'}),
    { dataField: 'customer.email_certified', caption: i18n.t('common.emailCertified') },
    {
      dataField: 'customer.identity_document_type_ref',
      caption: i18n.t('common.identityDocumentType'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedIdentityDocumentTypes() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.identity_document_number',
      caption: i18n.t('common.identityDocumentNumber'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.identity_document_date',
      caption: i18n.t('common.identityDocumentDate'),
      dataType: 'date',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.birth_date',
      caption: i18n.t('common.birthDate'),
      dataType: 'date',
      visible: false,
      showInColumnChooser: false
    },
  ]
}
